/**
 * My Insights — self-service analytics (Phase 7B/7C).
 *
 * A broker sees their own scorecard; a company manager additionally sees their
 * team leaderboard. Both are server-gated: the API returns { available:false }
 * when the feature is off or the caller isn't a broker/company manager, so each
 * section simply hides itself. The page lives behind ProtectedRoute.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import { GooeyLoader } from '../../../components/ui/gooey-loader';
import { getAuthToken } from '../../../utils/authToken';
import { getMyBrokerInsights, getMyCompanyInsights, type BrokerInsights, type CompanyInsights } from '../../../services/marketInsightsService';
import { formatAmount } from '../../../utils/numberFormat';

const PERIODS: { key: string; en: string; ar: string }[] = [
  { key: '7d', en: '7d', ar: '7 أيام' },
  { key: '30d', en: '30d', ar: '30 يوم' },
  { key: '90d', en: '90d', ar: '90 يوم' },
  { key: '1y', en: '1y', ar: 'سنة' },
];

function Kpi({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-2xl font-bold text-slate-900 mt-1">{value}</div>
    </div>
  );
}

export default function MyInsightsPage() {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [broker, setBroker] = useState<BrokerInsights | null>(null);
  const [company, setCompany] = useState<CompanyInsights | null>(null);
  const num = (n?: number) => formatAmount(Number(n || 0));

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const token = getAuthToken();
    Promise.allSettled([getMyBrokerInsights(token, period), getMyCompanyInsights(token, period)])
      .then(([b, c]) => {
        if (!alive) return;
        setBroker(b.status === 'fulfilled' ? b.value : { available: false });
        setCompany(c.status === 'fulfilled' ? c.value : { available: false });
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [period]);

  const nothing = !loading && !(broker?.available) && !(company?.available);

  return (
    <div dir={isAr ? 'rtl' : 'ltr'} className="min-h-screen flex flex-col bg-white">
      <Navigation />
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-[var(--pv-color-primary)]">{isAr ? 'إحصاءاتي' : 'My Insights'}</h1>
          <div className="inline-flex gap-1 bg-slate-100 rounded-lg p-1">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className={`px-3 py-1.5 rounded-md text-sm ${period === p.key ? 'bg-violet-600 text-white' : 'text-slate-600'}`}
              >{isAr ? p.ar : p.en}</button>
            ))}
          </div>
        </div>

        {loading && <div className="flex justify-center py-24"><GooeyLoader /></div>}

        {nothing && (
          <div className="rounded-2xl border border-slate-200 bg-white px-6 py-16 text-center">
            <div className="text-4xl mb-3">📈</div>
            <h2 className="text-lg font-semibold text-[var(--pv-color-primary)]">{isAr ? 'لا تتوفر إحصاءات' : 'No insights available'}</h2>
            <p className="text-slate-500 mt-2 text-sm">
              {isAr
                ? 'تتوفر هذه الصفحة للوسطاء والشركات عندما تكون الميزة مفعّلة. تواصل مع الدعم لمزيد من المعلومات.'
                : 'This page is available to brokers and companies once the feature is enabled. Contact support for more.'}
            </p>
          </div>
        )}

        {!loading && broker?.available && (
          <section className="mb-10">
            <h2 className="text-lg font-semibold text-[var(--pv-color-primary)] mb-3">
              {isAr ? 'أدائي كوسيط' : 'My broker performance'}
              {broker.agent?.name ? <span className="text-slate-400 font-normal text-sm"> · {broker.agent.name}</span> : null}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label={isAr ? 'مشاهدات' : 'Views'} value={num(broker.engagement?.propertyViews)} />
              <Kpi label={isAr ? 'تواصل' : 'Contacts'} value={num(broker.engagement?.contacts)} />
              <Kpi label={isAr ? 'استفسارات' : 'Inquiries'} value={num(broker.engagement?.inquiries)} />
              <Kpi label={isAr ? 'مبيعات' : 'Sold'} value={num(broker.engagement?.soldCount)} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
              <Kpi label={isAr ? 'إجمالي العروض' : 'Total listings'} value={num(broker.listings?.total)} />
              <Kpi label={isAr ? 'نشطة' : 'Active'} value={num(broker.listings?.active)} />
              <Kpi label={isAr ? 'فيديو %' : 'With video %'} value={`${broker.listings?.pctVideo ?? 0}%`} />
              <Kpi label={isAr ? 'جولة افتراضية %' : 'With tour %'} value={`${broker.listings?.pctTour ?? 0}%`} />
            </div>
          </section>
        )}

        {!loading && company?.available && (
          <section>
            <h2 className="text-lg font-semibold text-[var(--pv-color-primary)] mb-3">
              {isAr ? 'أداء الشركة' : 'Company performance'}
              {company.company?.name ? <span className="text-slate-400 font-normal text-sm"> · {isAr ? (company.company.nameAr || company.company.name) : company.company.name}</span> : null}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label={isAr ? 'مشاهدات' : 'Views'} value={num(company.engagement?.propertyViews)} />
              <Kpi label={isAr ? 'تواصل' : 'Contacts'} value={num(company.engagement?.contacts)} />
              <Kpi label={isAr ? 'فريق العمل' : 'Team size'} value={num(company.company?.teamSize)} />
              <Kpi label={isAr ? 'عروض نشطة' : 'Active listings'} value={num(company.listings?.active)} />
            </div>

            <h3 className="text-sm font-semibold text-slate-700 mt-6 mb-2">{isAr ? 'لوحة فريق العمل' : 'Team leaderboard'}</h3>
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                    <th className="text-start px-3 py-2">{isAr ? 'الموظف' : 'Employee'}</th>
                    <th className="text-start px-3 py-2">{isAr ? 'مشاهدات' : 'Views'}</th>
                    <th className="text-start px-3 py-2">{isAr ? 'تواصل' : 'Contacts'}</th>
                    <th className="text-start px-3 py-2">{isAr ? 'استفسارات' : 'Inquiries'}</th>
                    <th className="text-start px-3 py-2">{isAr ? 'عروض جديدة' : 'New listings'}</th>
                    <th className="text-start px-3 py-2">{isAr ? 'مبيعات' : 'Sold'}</th>
                  </tr>
                </thead>
                <tbody>
                  {(company.employees || []).map((e) => (
                    <tr key={e.agentId} className="border-t border-slate-100">
                      <td className="px-3 py-2 text-slate-800">{e.name || '-'}</td>
                      <td className="px-3 py-2">{num(e.views)}</td>
                      <td className="px-3 py-2">{num(e.contacts)}</td>
                      <td className="px-3 py-2">{num(e.inquiries)}</td>
                      <td className="px-3 py-2">{num(e.newListings)}</td>
                      <td className="px-3 py-2">{num(e.sold)}</td>
                    </tr>
                  ))}
                  {(!company.employees || company.employees.length === 0) && (
                    <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">{isAr ? 'لا توجد بيانات بعد.' : 'No data yet.'}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
